/*
 * ADD_VARIATION/INDEX.JSX - Add/Edit Variation Component
 * 
 * SUMMARY:
 * Main orchestration component for variation creation and editing.
 */

import React, { useEffect, useRef, useState } from "react";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import idx from 'idx';
import '../scss/task.scss';
import '../scss/variation.scss';
import './hierarchy.scss';

// Dialogs
import ProfitMarginLock from "../../../components/profitMarginLock-dialog";
import SaveTemplate from "../../../components/save-template";
import ListItemTemplate from "../../../components/list-template/item";
import CloseConfirmationDialog from "../confirmation-dialog";
import DeleteDialog from "../../../components/delete-dialog";
import AllocateResource from "../../timesheet/allocate-resource-dialog";
import ConfirmAllocateResource from "../../timesheet/confirm-allocate-resource-dialog";

// Actions
import { listCategories } from "../../../actions/inventory.action";
import { getSubcontractorsData } from "../utils/get-subcontractors";

// Custom hooks
import {
    useVariationForm,
    useVariationHierarchy,
    useVariationItems,
    useVariationApi,
    useVariationTimesheets
} from "./hooks";

// Presentational components
import VariationHeader from "./components/VariationHeader";
import VariationFormSection from "./components/VariationFormSection";
import VariationItemsSection from "./components/VariationItemsSection";
import VariationSummary from "./components/VariationSummary";
import VariationTimesheetTab from "./components/VariationTimesheetTab";
import { getInheritedFieldOpsExclusion } from "../utils/field-ops-inheritance";

/**
 * AddVariation - Main variation orchestration component
 */
export default function AddVariation({
    open,
    dialogClose,
    optionId,
    profitMargin,
    updatePermission,
    ...props
}) {
    const dispatch = useDispatch();

    // Redux selectors
    const projectSummary = useSelector(state => state.projectSummary) || {};
    const user = useSelector(state => state.user);
    const task = useSelector(state => state.task);
    const entitlements = useSelector(state => state.user.entitlements);
    const resources = useSelector(state => state.resources);
    const categoryList = useSelector(state => state.catalog.catalogs) || [];
    const subTaskArray = useSelector(state => state.task.task.subTaskArray) || [];

    // Local state
    const [editMode, setEditMode] = useState(!props.editOption);
    const [units, setUnits] = useState([]);
    const bottomElementRef = useRef(null);
    const [isBottomVisible, setIsBottomVisible] = useState(true);

    // Initialize hooks
    const formHook = useVariationForm({ profitMargin, optionId, dialogClose, editMode });
    const {
        fields,
        setFields,
        templateQty,
        setTemplateQty,
        excludeFromFieldOps,
        setExcludeFromFieldOps,
        taskState,
        setTaskState,
        showCloseConfirmDialog,
        inputChangeHandler,
        lockIconChange,
        profitMarginDialogHandler,
        handleSaveTemplateDialog,
        getFiles,
        handleCloseClick,
        handleCloseConfirm,
        handleCloseCancel,
    } = formHook;
    const currentStageId = idx(task, _ => _.task.stageId) || props.stageId;
    const isFieldOpsInheritedExcluded = getInheritedFieldOpsExclusion(
        idx(projectSummary, _ => _.sectionData) || [],
        currentStageId,
    );

    const hierarchyHook = useVariationHierarchy(
        subTaskArray,
        fields.profitMargin.value,
        fields.profitMarginType.value
    );
    const {
        expandedSections,
        setExpandedSections,
        selectedParent,
        setSelectedParent,
        buildHierarchy,
        toggleSection,
        validatePlacement,
        getAllowedChildType,
        calculateSectionTotals,
        expandParentSection,
    } = hierarchyHook;

    const itemsHook = useVariationItems({
        subTaskArray,
        fields,
        resources,
        projectSummary,
        selectedParent,
        setSelectedParent,
        taskState,
        setTaskState,
        validatePlacement,
        expandParentSection,
        getIdsToDelete: hierarchyHook.getIdsToDelete
    });
    const {
        deleteSubTask,
        sectionDeleteState,
        setSectionDeleteState,
        handleAddTask,
        loadItemFromTemplate,
        refreshHandler,
        deleteSubTaskHandler,
        getDeleteSubTask,
        filterLabor,
        handleItemTemplateDialog,
        handleSaveTemplateDialog: handleSaveItemTemplate,
        handleSectionDelete,
        confirmSectionDelete,
        cancelSectionDelete,
        handleCopyItem,
        handleMoveItem,
    } = itemsHook;

    const apiHook = useVariationApi({
        optionId,
        profitMargin,
        resources,
        projectSummary,
        fields,
        setFields,
        setTemplateQty,
        excludeFromFieldOps,
        setExcludeFromFieldOps,
        isFieldOpsInheritedExcluded,
        setTaskState,
        setUnits,
        subTaskArray,
        deleteSubTask,
        props
    });
    const {
        initLoad,
        submitHandler: apiSubmitHandler,
    } = apiHook;

    const timesheetsHook = useVariationTimesheets({
        optionId,
        projectId: props.projectId,
        user,
        getStages: props.getStages
    });
    const {
        allocationState,
        getUsersList,
        getAllocationList,
        mapUserName,
        handleAllocationDialog,
        closeAllocationDialog,
        handleEditTimesheet,
        allocateResourceHandler,
        allocateScheduleHandler,
        closeConfirmDialog,
        deleteAllocationHandler,
        deleteDialogHandler,
    } = timesheetsHook;

    /**
     * Initialize component
     */
    useEffect(() => {
        if (categoryList.length === 0) dispatch(listCategories()).catch(e => console.error(e));
        if (_.isEmpty(idx(projectSummary, _ => _.subcontractors))) {
            getSubcontractorsData({ type: 'sub-contractor', dispatch });
        }
        initLoad();
        getUsersList();
        if (optionId) getAllocationList();
        setupViewportObserver();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Setup viewport observer for sticky buttons
     */
    const setupViewportObserver = () => {
        const observer = new IntersectionObserver(
            ([entry]) => setIsBottomVisible(entry.isIntersecting),
            { root: null, threshold: 0.1 }
        );
        if (bottomElementRef.current) observer.observe(bottomElementRef.current);
        return () => {
            if (bottomElementRef.current) observer.unobserve(bottomElementRef.current);
        };
    };

    /**
     * Check if user can edit
     */
    const enableEditBtn = () => {
        const option = projectSummary.optionData?.find(option => option.id === optionId);
        const variationStatus = option?.variationStatus || 
            (option?.proposal_selected === false ? 'DRAFT' : 
             option?.proposal_status || 'DRAFT');
        
        const isVariationBeingRevised = props.action === "revise";
        const isNewVariation = props.action === "new";
        const isVariationBeingEdited = props.action === "edit";
        
        // Disable editing for statuses that don't allow editing in view-variation
        const restrictedStatuses = ['PENDING', 'SUBMITTED', 'APPROVED'];
        if (restrictedStatuses.includes(variationStatus)) {
            return false;
        }
        
        // Allow editing for DRAFT and REJECTED
        const canEdit = ['DRAFT', 'REJECTED'].includes(variationStatus);
        
        return (
            canEdit ||
            isVariationBeingRevised ||
            isNewVariation ||
            isVariationBeingEdited
        );
    };

    return (
        <>
            {/* Allocation Dialogs */}
            {allocationState.deleteDialogState && (
                <DeleteDialog
                    open={allocationState.deleteDialogState}
                    id={allocationState.allocationId}
                    deleteHandler={() => deleteAllocationHandler(allocationState.allocationId)}
                    dialogClose={deleteDialogHandler}
                    moduleName="Allocation"
                    description="Allocation"
                />
            )}

            {!allocationState.deleteDialogState && allocationState.showAllocationDialog && (
                <AllocateResource
                    open={allocationState.showAllocationDialog}
                    dialogClose={closeAllocationDialog}
                    usersList={allocationState.usersList}
                    allocateResource={allocateResourceHandler}
                    allocationId={allocationState.allocationId}
                    allocationType={allocationState.allocationType}
                    moduleName={"VariationDetails"}
                    editTimesheet={allocationState.editTimesheet}
                    allocationDate={allocationState.allocationDate}
                    taskId={optionId}
                    projectId={props.projectId}
                    userId={allocationState.userId}
                />
            )}

            {allocationState.confirmDialogState && (
                <ConfirmAllocateResource
                    open={allocationState.confirmDialogState}
                    data={allocationState.confirmData}
                    getAllocations={getAllocationList}
                    dialogClose={closeConfirmDialog}
                    allocationType={allocationState.allocationType}
                    allocateSchedule={allocateScheduleHandler}
                />
            )}

            {/* Section Delete Dialog */}
            <DeleteDialog
                open={sectionDeleteState.open}
                id={sectionDeleteState.idsToDelete}
                deleteHandler={confirmSectionDelete}
                dialogClose={cancelSectionDelete}
                moduleName="Section"
                description={`Section ${sectionDeleteState.description}`}
            />

            {/* Main Modal */}
            <Modal
                show={open}
                onHide={handleCloseClick}
                dialogClassName="modal-90w create-modal"
                data-cy={'variation-modal'}
            >
                <VariationHeader
                    editMode={editMode}
                    action={props.action}
                    editOption={props.editOption}
                />

                <Modal.Body>
                    <div className="modal-body-container variation-modal">
                        {/* Profit Margin Lock Dialog */}
                        <ProfitMarginLock
                            open={taskState.profitMarginDialogState}
                            lockIconChange={lockIconChange}
                            dialogClose={profitMarginDialogHandler}
                            moduleName="Variation"
                            modalHeading={"Profit Margin Lock Changes"}
                            text={"profit margin"}
                            data-cy="profit-margin-lock-changes"
                            message={taskState.showlockIcon ?
                                'Are you sure you want to change the Project default profit margin? - Any items using default profit margins will also be updated to this value' : ''}
                        />

                        {/* Save Template Dialog */}
                        <SaveTemplate
                            open={taskState.saveTemplateDialog}
                            dialogClose={() => handleSaveTemplateDialog()}
                            data={taskState.saveTemplateData.data}
                            type={taskState.saveTemplateData.type}
                        />

                        {/* List Item Template Dialog */}
                        {taskState.showItemTemplateDialog && (
                            <ListItemTemplate
                                open={taskState.showItemTemplateDialog}
                                dialogClose={handleItemTemplateDialog}
                                addHandler={loadItemFromTemplate}
                                hideMaterialIssues={true}
                            />
                        )}

                        {/* Form Fields */}
                        <VariationFormSection
                            fields={fields}
                            editMode={editMode}
                            taskState={taskState}
                            inputChangeHandler={inputChangeHandler}
                            excludeFromFieldOps={excludeFromFieldOps}
                            setExcludeFromFieldOps={setExcludeFromFieldOps}
                            isFieldOpsInheritedExcluded={isFieldOpsInheritedExcluded}
                            profitMarginDialogHandler={profitMarginDialogHandler}
                            getFiles={getFiles}
                            task={task}
                            templateQty={templateQty}
                            enableEditBtn={enableEditBtn}
                            refreshHandler={refreshHandler}
                            renderMultiSelectJSX={props.renderMultiSelectJSX}
                        />

                        {/* Sticky Save/Cancel Buttons */}
                        {!isBottomVisible && (
                            <div className="position-absolute d-flex gap-2 end-0">
                                {editMode && enableEditBtn() && (
                                    <>
                                        <Button className="ms-auto task-save-btn secondarybtn" data-cy="save-variation" onClick={() => apiSubmitHandler(dialogClose)}>SAVE</Button>
                                        <Button className="secondarybtn task-cancel-btn cancelbtn" data-cy="cancel-variation" onClick={dialogClose}>CANCEL</Button>
                                    </>
                                )}
                                {!editMode && enableEditBtn() && (
                                    <Button className="ms-auto me-3 secondarybtn" data-cy="edit-variation" onClick={() => setEditMode(true)}>Edit Variation</Button>
                                )}
                            </div>
                        )}

                        {/* Tabs */}
                        <Tabs defaultActiveKey="items" id="variation-tabs" className="mb-2" data-cy="variation-tabs">
                            <Tab eventKey="items" title="Items" data-cy="items-tab">
                                {/* Items and Hierarchy */}
                                <VariationItemsSection
                                    subTaskArray={subTaskArray}
                                    fields={fields}
                                    editMode={editMode}
                                    enableEditBtn={enableEditBtn}
                                    hierarchyFunctions={{
                                        expandedSections,
                                        buildHierarchy,
                                        toggleSection,
                                        getAllowedChildType,
                                        calculateSectionTotals
                                    }}
                                    itemFunctions={{
                                        handleAddTask,
                                        handleItemTemplateDialog,
                                        handleSaveTemplateDialog,
                                        deleteSubTaskHandler,
                                        getDeleteSubTask,
                                        handleSectionDelete,
                                        handleCopyItem,
                                        handleMoveItem
                                    }}
                                    projectSummary={projectSummary}
                                    categoryList={categoryList}
                                    unitList={units}
                                    laborsList={props.labors}
                                    wTemplatePermission={props.wTemplatePermission}
                                />

                                {/* Totals Table */}
                                <VariationSummary
                                    task={task}
                                    fields={fields}
                                    editMode={editMode}
                                    taskState={taskState}
                                    inputChangeHandler={inputChangeHandler}
                                    profitMarginDialogHandler={profitMarginDialogHandler}
                                />
                            </Tab>

                            {/* Timesheets Tab */}
                            {entitlements.scheduleMenu && props.projectType !== "TENDER" && (
                                <Tab eventKey="user" title="Timesheets">
                                    <VariationTimesheetTab
                                        allocates={allocationState.allocates}
                                        mapUserName={mapUserName}
                                        handleEditTimesheet={handleEditTimesheet}
                                        deleteDialogHandler={deleteDialogHandler}
                                        handleAllocationDialog={handleAllocationDialog}
                                        editMode={editMode}
                                        optionId={optionId}
                                    />
                                </Tab>
                            )}
                        </Tabs>

                        {/* Bottom Action Buttons */}
                        <div className="col-12 d-flex mt-3 gap-2" ref={bottomElementRef}>
                            {props.wTemplatePermission && (
                                <Button onClick={() => handleSaveTemplateDialog('task', idx(task, _ => _.task))} className="primarybtn">
                                    SAVE AS TASK TEMPLATE
                                </Button>
                            )}

                            {editMode && enableEditBtn() && (
                                <>
                                    <Button className="ms-auto task-save-btn secondarybtn" data-cy="save-variation" onClick={() => apiSubmitHandler(dialogClose)}>SAVE</Button>
                                    <Button className="secondarybtn task-cancel-btn cancelbtn" data-cy="cancel-variation" onClick={handleCloseClick}>CANCEL</Button>
                                </>
                            )}

                            {!editMode && enableEditBtn() && (
                                <Button className="ms-auto me-3 secondarybtn" data-cy="edit-variation" onClick={() => setEditMode(true)}>
                                    Edit Variation
                                </Button>
                            )}
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Close Confirmation Dialog */}
            <CloseConfirmationDialog
                show={showCloseConfirmDialog}
                onHide={handleCloseCancel}
                onCancel={handleCloseCancel}
                onClose={handleCloseConfirm}
            />
        </>
    );
}
